import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RbacService, OauthService } from '@stg-js-ngx-core/oauth';
import { fromPromise } from 'rxjs/internal-compatibility';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SERVICE_PHRASE } from '../constants/constants';
import { LoggedInUserDetailsService } from './logged-in-user-details.service';
import { Observable, of } from 'rxjs';
import * as moment from "moment";
import { HTTP_STATUS, USER_STATUSES } from '../constants/app.constants';

declare var aaimsCommonService: any;
declare var selfServiceValues: any;

@Injectable({
  providedIn: 'root'
})
export class SyncopeService {
  syncopeConfig: any = [];
  private userObj: any;
  constructor(
    private http: HttpClient,
    private oauthService: OauthService,
    private rbacService: RbacService,
    private loggedInUserDetailsService: LoggedInUserDetailsService) { }

  getUsers(
    realm: string,
    page: number = 1,
    size: number = 25,
    sort: string = 'username asc',
    fiql: string = ''
  ) {
    return fromPromise(aaimsCommonService.getUsers(this.oauthService.oAuthToken,page, size, sort, realm, fiql))
      .pipe(map((res: any) => this.modifyUsersData(res.data, null)));
  }

  getUsersV2(
    realm: string,
    page: number = 1,
    size: number = 25,
    orderBy: string = JSON.stringify([{col: 'email', sort: '1'}]),
    filter: string = '',
    orgs: any = null
  ) {
    return fromPromise(aaimsCommonService.getUsersV2(this.oauthService.oAuthToken,page, size, orderBy, realm, filter ))
      .pipe(map((res: any) => this.modifyUsersData(res.data, orgs)));
  }

  getNumbersV2() {
    return fromPromise(aaimsCommonService.getNumbersV2(this.oauthService.oAuthToken))
    .pipe(catchError(err => {console.error(err);
      return of(null);
    }));
  }

  getSyncopeUser(key?: string){
    return fromPromise(aaimsCommonService.getUser(this.oauthService.oAuthToken,key ? key : this.rbacService.username)).pipe(catchError(err => {
      console.error(err);
      return of(null);
    }));
  }

  addSyncopeUser(userInfo, realm, auxClass, resources) {
    this.sanitizeUserInfo(userInfo);
    return aaimsCommonService.addUser(this.oauthService.oAuthToken,  this.formatUserData(userInfo), realm, auxClass, userInfo.email,resources);
  }

  updateSyncopeUser(userInfo, editProfileFlag, realm, auxClass, resources) {
    this.sanitizeUserInfo(userInfo);
    return aaimsCommonService.changeUser(
      this.oauthService.oAuthToken,
      userInfo.key,
      this.formatUserData(userInfo),
      editProfileFlag,
      realm,
      auxClass,
      userInfo.username,
      resources);
  }

  set syncopeUserObj(obj){
    this.userObj = this.formatUserObj(obj);
  }

  get syncopeUserObj(){
    return this.userObj;
  }

  formatUserObj(result: any): any {
    if (!result?.plainAttrs) return {};

    const user = this.mapPlainAttributes(result.plainAttrs);
    return {
      ...user,
      ...this.mapUserProperties(result)
    };
  }

  private mapPlainAttributes(plainAttrs: any[]): any {
    return plainAttrs.reduce((user, attr) => {
      user[attr.schema] = attr.values[0];
      return user;
    }, {});
  }

  private mapUserProperties(result: any): any {
    return {
      mustChangePassword: result.mustChangePassword,
      status: result.status,
      key: result.key,
      securityQuestionKey: result.securityQuestion,
      username: result.username,
      realm: result.realm,
      accountSuspended: result.status?.toLowerCase() === 'suspended'
    };
  }

  changeUserStatus(userInfo, accountStatus, resources): Promise<any> {
    this.sanitizeUserInfo(userInfo);
    return aaimsCommonService.changeUserStatus(this.oauthService.oAuthToken, userInfo.key, accountStatus, resources);
  }

  resetSecurity(userInfo, resources, auxClass): Promise<any> {
    this.sanitizeUserInfo(userInfo);
    const data = {
      "username": userInfo.username,
      "resetSecurity": userInfo.resetSecurity,
      "guid": userInfo.guid
    };
    return aaimsCommonService.resetSecurity(this.oauthService.oAuthToken, userInfo.key, data, resources, auxClass);
  };

  modifyUsersData(data:any, orgs:any) {
    let usersData: any = {};
    usersData.metadata = {};
    usersData.metadata.http_status_code = HTTP_STATUS.SUCCESS.toString();
    usersData.data = [];

    const secObjects = this.rbacService.rbacProfile.secured_objects;
    if (this.rbacService.currentRoleName === 'User' || secObjects.length === 0) {
      usersData.metadata.resultCount = 0;
      return usersData;
    }

    for (let t = 0; t < secObjects.length; t++) {
      if (secObjects[t].object_name === 'User Administration' && secObjects[t].access_type === 'BLOCK') {
        usersData.metadata.resultCount = 0;
        return usersData;
      }
    }

    usersData.metadata.resultCount = data.totalCount;
    let syncopeUsersData:any = {};

    for (let x = 0; x < data.data.length; x++) {
      const result = data.data[x];
      syncopeUsersData = result;

        if (result.hireDate) {
          syncopeUsersData.hireDate = result.hireDate ? moment(result.hireDate).format('MM/DD/YYYY') : null;
        }

      syncopeUsersData.account_locked = syncopeUsersData.status.toUpperCase() === 'SUSPENDED';

      syncopeUsersData.key = result.aaimsId;
      syncopeUsersData.securityQuestionKey = result.securityQuestionId;

      if (
        (
          data.data[x].securityQuestionId === null ||
          (syncopeUsersData.mustChangePassword && syncopeUsersData.mustChangePassword.toString().toLowerCase() === 'true')
        ) &&
        syncopeUsersData.status.toUpperCase() === 'ACTIVE'
      ) {
        syncopeUsersData.account_locked = false;

        if (syncopeUsersData.allowIDMNotifications && syncopeUsersData.allowIDMNotifications.toString().toLowerCase() ==='false'){
          syncopeUsersData.status = USER_STATUSES.ACTIVE;
        }
        else{
        syncopeUsersData.status = USER_STATUSES.USER_ACTION_REQUIRED;
        }
      }

      if(orgs){
        for (let k = 0; k < orgs.length; k++) {
          if(syncopeUsersData.organizationName === orgs[k].teamName){
            syncopeUsersData.organizationDescription=orgs[k].organization;
          }
        }
      }

      usersData.data.push(syncopeUsersData);
      syncopeUsersData = {};
    }

    return usersData;
  }

  getSecurityQuestionsByUser(userName): Promise<any> {
    return aaimsCommonService.getSecurityQuestionByUser(this.oauthService.oAuthToken, userName);
  }

  verifyGuid(guid, userKey, userName): Promise<any> {
    return aaimsCommonService.verifyGuidByUserDetails(this.oauthService.oAuthToken, guid, userKey, userName);
  };

  getSecurityQuestions(): Promise<any> {
    return aaimsCommonService.getSecurityQuestions(this.oauthService.oAuthToken);
  };

  changeSecurityQuestion(key, question, answer): Promise<any> {
    return aaimsCommonService.changeSecurityQuestion(this.oauthService.oAuthToken,key, question, answer);
  };

  changePassword(userKey, password, resources): Promise<any> {
    return aaimsCommonService.changePassword(this.oauthService.oAuthToken, userKey, password, resources);
  };

  setPassword(guid, userName, userKey, password, securityQuestionKey, securityAnswer, resources, auxClass): Promise<any> {
    return aaimsCommonService.setPassword(this.oauthService.oAuthToken, guid, userName, userKey, password, securityQuestionKey, securityAnswer, resources, auxClass);
  };

  confirmPasswordReset(token, newPassword): Promise<any> {
    return aaimsCommonService.confirmPasswordReset(this.oauthService.oAuthToken, token, newPassword);
  };

  requestPasswordReset(username, securityAnswer): Promise<any> {
    return aaimsCommonService.requestPasswordReset(this.oauthService.oAuthToken,username,securityAnswer);
  };

  getConfigurations(selfService?: boolean) {
    let url = environment.urls.getSyncopeConfigurations;
    let localHeaders = {};
    if (selfService) {
      url = environment.urls.getSelfSyncopeConfigurations;
      localHeaders['Authorization'] = 'Bearer ' + selfServiceValues.serviceValue(SERVICE_PHRASE, url, null, this.loggedInUserDetailsService.calculatedOffset);
    }

    return this.http.get(url, { headers: localHeaders })
      .pipe(map((res: any) => {
        res = selfServiceValues.extractJSON(SERVICE_PHRASE, res.result);
        this.syncopeConfig = res;
        return res;
      }), catchError(err => { console.error(err); return of(null)}));
  }

  private sanitizeUserInfo(userInfo) {
    if (userInfo && userInfo.username && userInfo.username.indexOf("@") >= 0) {
      userInfo.username = userInfo.username.toLowerCase();
    }

    if (userInfo && userInfo.email) {
      userInfo.email = userInfo.email.toLowerCase();
    }
  }

  private formatUserData(userInfo: any): any {
    const excludeFields = ['status', 'key', 'securityQuestionKey', 'organization'];
    return Object.keys(userInfo)
      .filter(key => !excludeFields.includes(key))
      .reduce((obj, key) => {
        obj[key] = userInfo[key];
        return obj;
      }, {});
  }

resyncUsers(userNames): Observable<any> {
  let url = environment.urls.resyncPernos;
  userNames.forEach(user => {
      user.employee_number = user.employee_number.padStart(8, '0');
  });
  return this.http.post(url, userNames,  { responseType: 'text' })
      .pipe(
          map((response: any) => {
              if (response) {
                  return this.syncopeResyncPerNo(response);
              }
              return null;
          }),
          catchError(error => {
              console.error("Error in resyncUsers:", error);
              throw {
                  success: false,
                  message: error.message || 'Resync failed',
                  error: error
              };
          })
      );
}

  private syncopeResyncPerNo(data) {
    var responses = [];
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(data, 'text/xml');
      const employeeNumbers = xmlDoc.getElementsByTagName('employee_number');
      Array.from(employeeNumbers).forEach(element => {
        if (element.textContent) {
          responses.push(element.textContent);
        }
      });
    } catch (error) {
      console.error('Error parsing XML:', error);
    }
    return responses;
  }
}
